import { defineStore } from 'pinia'
import axiosService from '~/services/axios.service'
import { SESSION_EXPIRED_REASON, isSessionCurrent } from '~/utils/session'
import { useConveyancingCaseStore } from './conveyancing-case'

// Upper bound on the background token revocation when a session has lapsed.
const REVOKE_TOKEN_TIMEOUT_MS = 5000

export const useAuthStore = defineStore({
  id: 'auth',
  state: () => ({
    access_token: null,
    token_type: 'Bearer',
    expires_in: null,
    client: null,
    // Token of the session expireSession() last cleared, so a delayed answer for it can be
    // told apart from one for a session the client signed out of. Never persisted.
    expiredSessionHeader: null,
  }),
  getters: {
    authorizationHeader() {
      return `${this.token_type} ${this.access_token}`
    },
    isAuthenticated() {
      return !!this.access_token
    },
  },
  actions: {
    async login(formData) {
      return axiosService
        .post('/login', formData)
        .then((response) => {
          // Store token immediately - response is flat, not nested under 'token'
          this.access_token = response.access_token
          this.token_type = response.token_type
          this.expires_in = response.expires_at

          return {
            requires_2fa_verification: response.requires_2fa_verification,
            channels: response.channels || [],
            phone_hint: response.phone_hint,
            email_hint: response.email_hint,
            message: response.message,
          }
        })
        .catch((err) => {
          throw err
        })
    },
    async check2FAStatus() {
      return axiosService
        .get('/2fa/check-status')
        .then((response) => {
          return {
            verified: response.verified,
            reason: response.reason ?? null,
            channels: response.channels || [],
            phone_hint: response.phone_hint,
            email_hint: response.email_hint,
          }
        })
        .catch((err) => {
          throw err
        })
    },
    async verifyOtp(data) {
      return axiosService
        .post('/2fa/verify-otp', data)
        .then((response) => {
          return response
        })
        .catch((err) => {
          throw err
        })
    },
    async resendOtp() {
      return axiosService
        .post('/2fa/resend-otp')
        .then((response) => {
          return response
        })
        .catch((err) => {
          throw err
        })
    },
    // The answer is kept only for the session that asked: a sign-out or a newer sign-in
    // during the request must not have the earlier client's data written into the store.
    async fetchClient() {
      const sessionHeader = this.authorizationHeader

      return axiosService
        .get(`/client`)
        .then((response) => {
          if (!isSessionCurrent(this, sessionHeader)) return

          this.client = response

          useConveyancingCaseStore().conveyancingCase = response.conveyancing_case
        })
        .catch((err) => {
          throw err
        })
    },
    // Also drops the persisted case data so the next client on this browser never sees it.
    clearSession() {
      this.access_token = null
      this.token_type = 'Bearer'
      this.expires_in = null
      this.client = null
      this.expiredSessionHeader = null

      useConveyancingCaseStore().$reset()
    },
    async logout() {
      return await axiosService
        .post(`/logout`)
        .then(() => {
          this.clearSession()

          navigateTo('/login')
        })
        .catch((err) => {
          this.clearSession()

          navigateTo('/login')
        })
    },
    // The token's OTP state has gone (ClientAPI answers session-lapsed or a 2FA 403).
    // Drop the stored session straight away and revoke the token in the background,
    // so reaching the sign-in page never waits on the API. `authorizationHeader` names the
    // session being expired: a call for a session that a newer sign-in has since replaced
    // does nothing, so a stale failure can never revoke the new token.
    async expireSession(authorizationHeader = this.authorizationHeader) {
      if (this.isAuthenticated) {
        if (authorizationHeader !== this.authorizationHeader) return

        axiosService
          .post(`/logout`, null, { headers: { Authorization: authorizationHeader }, timeout: REVOKE_TOKEN_TIMEOUT_MS })
          .catch(() => {})
      }

      this.clearSession()
      this.expiredSessionHeader = authorizationHeader

      return navigateTo({ path: '/login', query: { reason: SESSION_EXPIRED_REASON } })
    },
  },
  persist: { omit: ['expiredSessionHeader'] },
})
